/**
 * delivery-logic.js — decisiones puras del picker de entrega.
 *
 * POR QUÉ EXISTE: la lógica vivía adentro del IIFE `initDelivery()` de theme.js,
 * mezclada con el DOM y sin exports, así que no había forma de testearla. Acá
 * queda todo lo que se puede decidir sin tocar el DOM: qué pasos mostrar, qué
 * días habilitar, qué texto lleva cada franja y cómo se arman las consultas.
 *
 * POR QUÉ ES UN SCRIPT CLÁSICO Y NO UN MÓDULO ESM: el tema no tiene bundler y
 * carga `theme.js` con `<script src=... defer>`. Un `<script type="module">`
 * cambiaría el orden de ejecución (los módulos son siempre diferidos y corren
 * después de los clásicos) y obligaría a resolver los imports contra la CDN de
 * Shopify con URLs completas. Se resuelve con un único global,
 * `window.PlusPetDelivery`, que theme.js consume y que vitest lee importando
 * este archivo por su efecto colateral. Sin `export` ni `module.exports`: así el
 * mismo archivo sirve tal cual en el navegador y en los tests.
 *
 * MODELO: desde la migración a paridad con Zapiet la configuración de agenda
 * (`fulfillment`) cuelga de la ubicación + el método, no de la zona. La zona
 * sólo define el área de cobertura del envío, y el retiro ya no devuelve zonas
 * sino ubicaciones.
 */
(function (global) {
  'use strict';

  var DEFAULT_FULFILLMENT = {
    enabled: true,
    datePickerEnabled: true,
    timePickerEnabled: true,
    availabilityMode: 'every_day',
    weekdays: [],
    horizonDays: null,
    daysShown: null,
    blackoutDates: [],
    cutoffPassed: false,
  };

  // El backend devuelve snake_case; adentro del tema se usa camelCase.
  // Si `fulfillment` viene null (bridge viejo, o tienda sin configuración
  // guardada) se asume todo habilitado: no poder leer la configuración no puede
  // dejar al comprador sin poder elegir nada.
  function normalizeFulfillment(raw) {
    if (!raw) return Object.assign({}, DEFAULT_FULFILLMENT, { weekdays: [] });
    return {
      enabled: raw.enabled !== false,
      datePickerEnabled: raw.date_picker_enabled !== false,
      timePickerEnabled: raw.time_picker_enabled !== false,
      availabilityMode: raw.availability_mode || 'every_day',
      weekdays: Array.isArray(raw.weekdays) ? raw.weekdays : [],
      horizonDays: raw.horizon_days == null ? null : Number(raw.horizon_days),
      // Cuántos días ofrece el carrito: sólo viene con el tope en días
      // hábiles ("siempre 4 opciones"); sin él, el tema usa los suyos.
      daysShown: Number(raw.days_shown) > 0 ? Number(raw.days_shown) : null,
      // Pistas para pintar el calendario sin tener que consultar día por día:
      // los días que el comerciante bloqueó y si hoy ya pasó la hora de corte.
      blackoutDates: Array.isArray(raw.blackout_dates) ? raw.blackout_dates : [],
      cutoffPassed: raw.cutoff_passed === true,
    };
  }

  // Qué se elige en el paso 2 según el método: en envío una zona de cobertura,
  // en retiro una ubicación física (con su dirección). El contrato viejo
  // —retiro devolvía zonas— ya no existe: en pickup `zones` viene vacío.
  function methodOptions(data, method) {
    var payload = data || {};
    if (method === 'pickup') {
      return {
        kind: 'location',
        options: (payload.locations || []).map(function (loc) {
          return {
            id: loc.id,
            name: loc.name || '',
            detail: loc.address || '',
            notes: loc.notes || loc.more_information || '',
            // Los campos sueltos viajan al carrito como Pickup-Location-*.
            location: loc,
          };
        }),
      };
    }
    return {
      kind: 'zone',
      options: (payload.zones || []).map(function (zone) {
        var option = { id: zone.id, name: zone.name || '', detail: '', notes: '' };
        if (zone.fulfillment) option.fulfillment = zone.fulfillment;
        return option;
      }),
    };
  }

  function optionLabel(option) {
    if (!option) return '';
    return option.detail ? option.name + ' — ' + option.detail : option.name;
  }

  function methodNoun(method) {
    return method === 'pickup' ? 'El retiro' : 'El envío a domicilio';
  }

  // Un método apagado o bloqueado por el carrito se explica con palabras: una
  // grilla vacía sin motivo se lee como "está roto".
  function unavailableMessage(args) {
    var fulfillment = args.fulfillment || DEFAULT_FULFILLMENT;
    if (fulfillment.enabled === false) {
      return methodNoun(args.method) + ' no está disponible por ahora.';
    }
    var blocked = args.blockedBy || [];
    if (blocked.length) {
      return 'No se puede con este método: ' + blocked.join(', ') + '.';
    }
    return null;
  }

  function emptyOptionsMessage(method) {
    return method === 'pickup'
      ? 'No hay puntos de retiro disponibles.'
      : 'Por ahora no cubrimos entregas en este código postal.';
  }

  // Qué pasos del acordeón se muestran. El paso "postal" es el selector del
  // paso 2: en envío es el código postal, y en retiro sólo aparece cuando hay
  // más de una ubicación para elegir.
  function visibleSteps(args) {
    var fulfillment = args.fulfillment || DEFAULT_FULFILLMENT;
    var steps = ['method'];
    // El paso 2 existe con los dos métodos: en envío pide el código postal y
    // en retiro aloja las cards de los puntos (planilla CART 19). Sacárselo al
    // retiro dejaba la card sin hito y la numeración saltaba del método al día.
    if (args.method) steps.push('postal');
    if (fulfillment.datePickerEnabled) steps.push('day');
    if (fulfillment.timePickerEnabled) steps.push('time');
    return steps;
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function parseISO(iso) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return null;
    // Fecha LOCAL a propósito: con `new Date('2026-08-05')` el navegador
    // interpreta UTC y el día se corre según la zona horaria.
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }

  function toISO(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
  }

  // `min`/`max` del input de fecha. `horizon_days` es hasta cuándo se puede
  // pedir a futuro; sin horizonte no hay tope.
  function dateBounds(args) {
    var fulfillment = args.fulfillment || DEFAULT_FULFILLMENT;
    var today = parseISO(args.today) || new Date();
    var max = null;
    if (fulfillment.horizonDays != null) {
      var limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() + fulfillment.horizonDays);
      max = toISO(limit);
    }
    return { min: toISO(today), max: max };
  }

  // ¿Se puede elegir este día? Devuelve también el motivo para poder avisarlo.
  function dayState(iso, fulfillment, today) {
    var f = fulfillment || DEFAULT_FULFILLMENT;
    var date = parseISO(iso);
    var from = parseISO(today) || new Date();
    if (!date) return { available: false, reason: 'past' };
    if (date < from) return { available: false, reason: 'past' };
    if (f.horizonDays != null) {
      var limit = new Date(from.getFullYear(), from.getMonth(), from.getDate() + f.horizonDays);
      if (date > limit) return { available: false, reason: 'horizon' };
    }
    // `specific_dates` guarda fechas puntuales que el payload no expone, así que
    // el tema no puede filtrarlas: habilita y la consulta de franjas decide.
    if (f.availabilityMode === 'specific_days' && f.weekdays.indexOf(date.getDay()) === -1) {
      return { available: false, reason: 'weekday' };
    }
    // El día que el comerciante bloqueó no se ofrece. Antes se veía clickeable y
    // devolvía "Sin horarios para esa fecha", que parece un error del sitio.
    if ((f.blackoutDates || []).indexOf(iso) !== -1) {
      return { available: false, reason: 'blackout' };
    }
    // El corte es del día en curso: pasada esa hora hoy ya no entra, pero
    // mañana sí.
    if (f.cutoffPassed && iso === toISO(from)) {
      return { available: false, reason: 'cutoff' };
    }
    return { available: true, reason: null };
  }

  function unavailableDayMessage(reason) {
    if (reason === 'weekday') return 'Ese día no hacemos entregas.';
    if (reason === 'blackout') return 'Ese día no hay entregas.';
    if (reason === 'cutoff') return 'Por hoy ya cerramos los pedidos. Elegí otro día.';
    if (reason === 'horizon') return 'Todavía no se puede pedir para esa fecha.';
    if (reason === 'past') return 'Elegí una fecha a partir de hoy.';
    return '';
  }

  function zonesQuery(args) {
    var query = { shop: args.shop };
    if (args.method === 'pickup') {
      query.fulfillment_method = 'pickup';
    } else {
      if (args.postalCode) query.postal_code = args.postalCode;
      // La dirección es lo que habilita las zonas por polígono y por radio: el
      // backend geocodifica y prueba el punto contra la forma. Sin ella sólo
      // matchea la lista de códigos postales, y las otras dos formas que ofrece
      // el panel quedan de adorno.
      if (args.address) query.address = args.address;
    }
    // F37: el backoffice necesita saber qué hay en el carrito para descartar
    // métodos que algún producto no admite.
    if (args.variantIds && args.variantIds.length) query.variant_ids = args.variantIds;
    return query;
  }

  // El retiro no tiene zona: la agenda es la de la ubicación, así que va por
  // /delivery_windows con el método, sin id de zona en el path.
  function windowsRequest(args) {
    var query = {};
    var path;
    if (args.method === 'pickup') {
      path = '/delivery_windows';
      query.shop = args.shop;
      query.fulfillment_method = 'pickup';
    } else {
      path = '/delivery_zones/' + args.optionId + '/windows';
    }
    if (args.date) query.date = args.date;
    query.variant_ids = args.variantIds || [];
    return { path: path, query: query };
  }

  // `remaining` sólo viene cuando hay tope de pedidos configurado. Sin tope no
  // hay cupo que agotar; con tope en 0 la franja directamente no se ofrece.
  function offerableWindows(windows) {
    return (windows || []).filter(function (w) {
      return w.remaining == null || Number(w.remaining) > 0;
    });
  }

  function windowKey(w) {
    return String(w.id) + '@' + w.start_time + '-' + w.end_time;
  }

  function windowView(w) {
    var meta;
    if (w.remaining == null) meta = 'Disponible';
    else meta = Number(w.remaining) === 1 ? 'Queda 1' : 'Quedan ' + w.remaining;
    return { id: windowKey(w), label: w.start_time + '–' + w.end_time, meta: meta };
  }

  // Con el selector de hora apagado el comprador elige sólo el día, pero el
  // backoffice sigue confirmando el pedido por `_pluspet_window_id`: si no se
  // manda ninguna franja, el webhook ignora el pedido. Se elige la primera con
  // cupo, en silencio.
  function autoWindow(windows, fulfillment) {
    var f = fulfillment || DEFAULT_FULFILLMENT;
    if (f.timePickerEnabled) return null;
    return offerableWindows(windows)[0] || null;
  }

  function cartAttributes(args) {
    var isPickup = args.method === 'pickup';
    var attrs = {
      _pluspet_method: isPickup ? 'pickup' : 'delivery',
      _pluspet_shop_domain: args.shop,
      // Delivery::ConfirmFromOrder guarda delivery_zone_id sólo para envío: en
      // retiro la opción elegida es una ubicación (otra tabla), mandar su id
      // como zona apuntaría a una zona ajena.
      _pluspet_zone_id: isPickup ? '' : String(args.optionId),
      _pluspet_window_id: String(args.windowId),
      _pluspet_slot_date: args.date || '',
      _pluspet_cart_token: args.cartToken || '',
    };
    if (args.slotStart && args.slotEnd) {
      attrs._pluspet_slot_start = args.slotStart;
      attrs._pluspet_slot_end = args.slotEnd;
    }
    if (!isPickup && args.postalCode) {
      attrs._pluspet_address_postal_code = args.postalCode;
    }
    // Usuario 04/10: el backoffice reemplaza a Zapiet. Las plantillas de mail y
    // la etiqueta de Orderly Print leen sus atributos (Delivery-Date
    // 2026/10/05, Delivery-Time "10:00 - 14:00"; Pickup-* en retiros), y el
    // mail de confirmación sale al crearse el pedido. Los del otro método van
    // vacíos: Shopify los borra del carrito.
    var prefix = isPickup ? 'Pickup' : 'Delivery';
    var other = isPickup ? 'Delivery' : 'Pickup';
    attrs['Checkout-Method'] = attrs._pluspet_method;
    attrs[prefix + '-Date'] = args.date ? String(args.date).replace(/-/g, '/') : '';
    // Zapiet (prod 04/10, #270059MDZ) guarda sólo la hora de inicio del
    // retiro; el envío lleva la franja "10:00 - 14:00".
    if (isPickup) attrs[prefix + '-Time'] = args.slotStart || '';
    else attrs[prefix + '-Time'] = args.slotStart && args.slotEnd ? args.slotStart + ' - ' + args.slotEnd : '';
    attrs[other + '-Date'] = '';
    attrs[other + '-Time'] = '';
    // La confirmación de prod (04/10) dice dónde retirar con
    // Pickup-Location-Company / -Address-Line-1, y sale al crearse el pedido.
    var loc = isPickup ? (args.location || {}) : {};
    var countries = { AR: 'Argentina' };
    attrs['Pickup-Location-Company'] = loc.name || '';
    attrs['Pickup-Location-Address-Line-1'] = loc.address_line1 || '';
    attrs['Pickup-Location-City'] = loc.city || '';
    attrs['Pickup-Location-Region'] = loc.province || '';
    attrs['Pickup-Location-Postal-Code'] = loc.postal_code || '';
    attrs['Pickup-Location-Country'] = loc.country ? (countries[loc.country] || loc.country) : '';
    return attrs;
  }

  // Para vaciarlos junto con los _pluspet_* (cambio de método, CP o carrito vacío).
  var ZAPIET_ATTRIBUTE_KEYS = ['Checkout-Method', 'Delivery-Date', 'Delivery-Time', 'Pickup-Date', 'Pickup-Time',
    'Pickup-Location-Company', 'Pickup-Location-Address-Line-1', 'Pickup-Location-City', 'Pickup-Location-Region',
    'Pickup-Location-Postal-Code', 'Pickup-Location-Country'];

  // Rangos de código postal de las ciudades donde PlusPet reparte. Es una pista
  // para responderle al comprador sin ida y vuelta: la cobertura REAL la decide
  // el backend, que conoce las zonas cargadas. Por eso un CP dentro del rango
  // no afirma nada, sólo deja de molestar.
  var POSTAL_RANGES = [
    { from: 1000, to: 1900, city: 'Buenos Aires' },
    { from: 2000, to: 2199, city: 'Rosario' },
    { from: 5000, to: 5199, city: 'Córdoba' },
    { from: 5500, to: 5629, city: 'Mendoza' }
  ];

  // Estado del código postal que escribió el comprador: si sirve para consultar,
  // a qué ciudad cae y qué decirle si algo no cierra.
  //
  // Vacío no es inválido: es que todavía no escribió. Marcarlo en rojo apenas
  // abre el carrito es acusarlo de un error que no cometió.
  function normalizePostal(raw) {
    var value = String(raw == null ? '' : raw).trim().toUpperCase().replace(/\s+/g, '');
    // Places devuelve también el prefijo de provincia seguido del CP básico.
    // Un CPA completo conserva sus letras para zonas con cobertura específica.
    return /^[A-Z]\d{4}$/.test(value) ? value.slice(1) : value;
  }

  function postalState(raw) {
    var value = normalizePostal(raw);

    if (value === '') return { valid: false, city: null, message: null };

    // Lovable 23/09 (classifyPostalError): primero los signos, después el
    // formato. Se sigue aceptando el CPA completo (M5500ABC).
    if (/[^0-9A-Z]/.test(value)) {
      return { valid: false, city: null, message: 'Ingresá solo letras y números, sin signos. Ej: 5519.' };
    }
    if (!/^(?:\d{4}|[A-Z]\d{4}[A-Z]{3})$/.test(value)) {
      return { valid: false, city: null, message: 'Ingresá un código postal válido.' };
    }

    var n = Number(value.match(/\d{4}/)[0]);
    var match = null;
    for (var i = 0; i < POSTAL_RANGES.length; i++) {
      if (n >= POSTAL_RANGES[i].from && n <= POSTAL_RANGES[i].to) {
        match = POSTAL_RANGES[i];
        break;
      }
    }

    return {
      valid: true,
      city: match ? match.city : null,
      // Un rango estático nunca decide cobertura: consultar las zonas actuales.
      message: null
    };
  }

  // --- Buscador de código postal -------------------------------------------
  //
  // El comprador que no se sabe el CP escribe su dirección o usa su ubicación.
  // Las tres llamadas pasan por el backend porque la clave de Google es de
  // servidor y no puede viajar al storefront.

  var MIN_SUGGEST_LENGTH = 3;

  // Google factura cada tecleo por separado salvo que los agrupe un token de
  // sesión: con él, todos los autocomplete de UNA búsqueda más el detalle final
  // se cobran como una sola sesión. Nace al abrir el buscador y muere al elegir
  // una dirección, que es lo que cierra la sesión del lado de Google.
  function newSessionToken() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();

    // Fallback para navegadores sin randomUUID: lo único que importa es que sea
    // distinto en cada búsqueda.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function geocodingRequest(kind, args) {
    var opts = args || {};
    var query = { shop: opts.shop };

    if (kind === 'suggest') {
      var term = String(opts.query == null ? '' : opts.query).trim();
      // Con menos letras Places devuelve cualquier cosa: no vale ni el viaje ni
      // la cuota.
      if (term.length < MIN_SUGGEST_LENGTH) return null;

      query.query = term;
      if (opts.city) query.city = opts.city;
      if (opts.sessionToken) query.session_token = opts.sessionToken;
      return { path: '/geocoding/suggest', query: query };
    }

    if (kind === 'place') {
      if (!opts.placeId) return null;

      query.place_id = opts.placeId;
      // Sin el token acá la sesión no cierra: los tecleos se facturan sueltos
      // igual y el detalle se suma aparte.
      if (opts.sessionToken) query.session_token = opts.sessionToken;
      return { path: '/geocoding/place', query: query };
    }

    if (kind === 'reverse') {
      if (opts.lat == null || opts.lng == null) return null;

      // El reverse geocoding es otra API y no participa de la sesión de
      // autocompletado: mandarle el token no agrupa nada.
      query.lat = String(opts.lat);
      query.lng = String(opts.lng);
      return { path: '/geocoding/reverse', query: query };
    }

    return null;
  }

  function suggestionLabel(suggestion) {
    var s = suggestion || {};
    var main = s.main_text || '';
    return s.secondary_text ? main + ' — ' + s.secondary_text : main;
  }

  // Qué hacer con lo que contestó el backend. El mensaje sale de allá: repetir
  // los casos acá haría que el mismo error se cuente de dos maneras según por
  // dónde entre.
  function resolutionOutcome(payload) {
    var res = payload || {};

    if (!res.ok || !res.postal_code) {
      return {
        postal: null,
        address: null,
        message: res.message || 'No pudimos resolver esa dirección. Probá de nuevo.'
      };
    }

    // Google puede resolver un CP de cualquier punto del país. Que exista no
    // quiere decir que repartamos ahí.
    var coverage = postalState(res.postal_code);

    return {
      postal: normalizePostal(res.postal_code),
      address: res.formatted_address || null,
      message: coverage.message
    };
  }

  global.PlusPetDelivery = {
    normalizeFulfillment: normalizeFulfillment,
    methodOptions: methodOptions,
    optionLabel: optionLabel,
    unavailableMessage: unavailableMessage,
    emptyOptionsMessage: emptyOptionsMessage,
    visibleSteps: visibleSteps,
    dateBounds: dateBounds,
    dayState: dayState,
    unavailableDayMessage: unavailableDayMessage,
    zonesQuery: zonesQuery,
    windowsRequest: windowsRequest,
    offerableWindows: offerableWindows,
    windowView: windowView,
    windowKey: windowKey,
    autoWindow: autoWindow,
    cartAttributes: cartAttributes,
    ZAPIET_ATTRIBUTE_KEYS: ZAPIET_ATTRIBUTE_KEYS,
    postalState: postalState,
    normalizePostal: normalizePostal,
    newSessionToken: newSessionToken,
    geocodingRequest: geocodingRequest,
    suggestionLabel: suggestionLabel,
    resolutionOutcome: resolutionOutcome,
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
